/**
 * Widget de Audio FINAL (Force Reset Version)
 * - storageKey actualizado para forzar aparición del popup en pruebas.
 * - Popup con estilos '!important' para vencer cualquier CSS del tema.
 * - Posición derecha.
 */
class AudioWidget {
    constructor() {
        // --- CONFIGURACIÓN ---
        this.config = {
            ids: {
                audio: 'miAudioPersonalizado',
                container: 'audioContainer',
                btn: 'miBotonDeAudio',
                tooltip: 'audioTooltip',
                icon: 'audioIcon',
            },
            icons: {
                on: "https://cdn.shopify.com/s/files/1/0689/8135/8754/files/speaker-svgrepo-com.svg?v=1762725877",
                off: "https://cdn.shopify.com/s/files/1/0689/8135/8754/files/speaker-off-svgrepo-com.svg?v=1762725868"
            },
            // CAMBIO IMPORTANTE: Cambié el nombre para resetear la memoria en tus pruebas
            storageKey: 'audioPermission_v2025', 
            touchDragThreshold: 5,
            inactiveDelay: 2000
        };

        // --- ESTADO ---
        this.state = {
            isPlaying: false,
            permission: null,
            isDragging: false,
            isAdjustingVolume: false,
            isMobile: /Mobi|Android|iPhone|iPad|iPod|Windows Phone|IEMobile|BlackBerry|Opera Mini/i.test(navigator.userAgent) || (window.navigator.maxTouchPoints > 1),
            lastNonZeroVolume: 1,
            drag: { startX: 0, startY: 0, initialLeft: 0, initialTop: 0, hasMoved: false, dragJustEnded: false },
            slider: { trackHeight: 0, rect: null },
            wasPlayingBeforeHidden: false 
        };

        this.elements = {};
        this.unlockListenersAttached = false; 

        // Bindings
        this.updateDrag = this.updateDrag.bind(this);
        this.updateVolumeDrag = this.updateVolumeDrag.bind(this);
        this.handleResize = this.debounce(this.handleResize.bind(this), 200);
        this.unlockHandler = this.unlockHandler.bind(this); 
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this); 
    }

    // --- MÉTODOS PÚBLICOS ---

    init() {
        if (!this.cacheElements()) {
            console.warn('AudioWidget: Faltan elementos de audio/contenedor en el DOM.');
            return;
        }

        this.injectStyles();
        this.loadPermission();
        this.setupAudio();
        this.setupUI();
        this.createVolumeControl();

        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        // Lógica de decisión
        if (!this.state.permission) {
            this.showPermissionPopup();
        } else if (this.state.permission === 'granted') {
            this.playAudio(true);
        }
    }

    // --- GESTIÓN DE ELEMENTOS Y ESTILOS ---

    cacheElements() {
        const { ids } = this.config;
        this.elements.audio = document.getElementById(ids.audio);
        this.elements.container = document.getElementById(ids.container);
        this.elements.tooltip = document.getElementById(ids.tooltip);
        this.elements.icon = document.getElementById(ids.icon);
        this.elements.externalBtn = document.getElementById(ids.btn);

        if (this.elements.audio) {
            this.elements.audio.preload = "auto";
            this.state.lastNonZeroVolume = this.elements.audio.volume > 0 ? this.elements.audio.volume : 1;
            if (!this.state.isMobile) {
                this.elements.audio.volume = this.elements.audio.volume || 1; 
            }
        }
        return !!(this.elements.audio && this.elements.container);
    }

    injectStyles() {
        const css = `
            :root {
                --aw-safe-bottom: env(safe-area-inset-bottom, 20px);
            }
            #audioContainer {
                transform: scale(0.9);
                transform-origin: center center;
                transition: opacity 0.3s ease, transform 0.1s linear, top 0.3s ease, left 0.3s ease;
                opacity: 0.6;
                touch-action: none; 
                z-index: 2147483640;
                cursor: grab;
                position: fixed;
                bottom: calc(20px + var(--aw-safe-bottom)); 
                right: 20px; 
                left: auto;  
            }
            #audioContainer:hover, #audioContainer.is-active { opacity: 1; }
            #audioContainer.is-dragging { cursor: grabbing; transition: none; opacity: 1; }
            
            #audioTooltip {
                opacity: 0; pointer-events: none; transition: opacity 0.35s ease;
                right: 110%; left: auto; top: 50%; transform: translateY(-50%); position: absolute;
            }
            #audioTooltip.visible { opacity: 1; pointer-events: auto; }

            .volume-wrapper {
                position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%);
                height: 52px; width: 28px; display: flex; align-items: flex-end;
                justify-content: center; padding: 4px 0; z-index: 1;
                cursor: default; pointer-events: auto;
                opacity: 0; visibility: hidden;
                transition: opacity 0.2s, visibility 0s linear 0.2s; 
            }
            .volume-wrapper.is-visible {
                opacity: 1; visibility: visible; transition: opacity 0.2s, visibility 0s; 
            }
            .volume-track {
                position: relative; width: 10px; height: 100%; border-radius: 999px;
                background: rgba(0,0,0,0.18); box-shadow: 0 0 2px rgba(0,0,0,0.25);
                overflow: visible; 
            }
            .volume-level {
                position: absolute; left: 0; bottom: 0; width: 100%; border-radius: 999px;
                background: #000000; pointer-events: none;
            }
            .volume-handle {
                position: absolute; left: 50%; transform: translateX(-50%);
                width: 18px; height: 18px; border-radius: 50%; background: #fff;
                border: 1px solid rgba(0,0,0,0.4); box-shadow: 0 1px 3px rgba(0,0,0,0.35);
                bottom: 100%; cursor: grab; margin-bottom: -9px;
                touch-action: none;
            }
        `;
        const style = document.createElement('style');
        style.textContent = css;
        document.head.appendChild(style);
        
        this.setWidgetActive(false);
    }
    
    setWidgetActive(active) {
        this.elements.container.classList.toggle('is-active', active);
    }

    scheduleInactive() {
        if (!this.state.isDragging && !this.state.isAdjustingVolume) {
            setTimeout(() => {
                this.setWidgetActive(false);
            }, this.config.inactiveDelay);
        }
    }

    // --- LÓGICA DE AUDIO Y PERMISOS ---

    loadPermission() {
        try { this.state.permission = localStorage.getItem(this.config.storageKey); } catch (e) { }
    }

    savePermission(val) {
        this.state.permission = val;
        try { localStorage.setItem(this.config.storageKey, val); } catch(e){}
    }

    setupAudio() {} 

    playAudio(isAutoPlayAttempt = false) {
        const { audio } = this.elements;
        if (audio.readyState === 0) audio.load();
        
        const playPromise = audio.play();

        if (playPromise !== undefined) {
            playPromise.then(() => {
                this.state.isPlaying = true;
                this.updateIcon();
                if (!isAutoPlayAttempt) this.showTooltip();
                this.savePermission('granted');
                this.removeUnlockListeners();
            }).catch(err => {
                console.warn('AudioWidget: Autoplay bloqueado.', err);
                this.state.isPlaying = false;
                this.updateIcon();
                if (this.state.permission === 'granted') {
                    this.addUnlockListeners();
                }
            });
        }
    }

    addUnlockListeners() {
        if (this.unlockListenersAttached) return;
        document.addEventListener('touchstart', this.unlockHandler, { passive: true });
        document.addEventListener('click', this.unlockHandler, { passive: true });
        this.unlockListenersAttached = true;
    }

    removeUnlockListeners() {
        if (!this.unlockListenersAttached) return;
        document.removeEventListener('touchstart', this.unlockHandler);
        document.removeEventListener('click', this.unlockHandler);
        this.unlockListenersAttached = false;
    }

    unlockHandler() {
        this.playAudio(false); 
        this.removeUnlockListeners();
    }

    handleVisibilityChange() {
        if (!this.state.isMobile) return; 
        const { audio } = this.elements;
        if (document.hidden) {
            if (!audio.paused) {
                audio.pause();
                this.state.wasPlayingBeforeHidden = true;
            }
        } else {
            if (this.state.wasPlayingBeforeHidden) {
                setTimeout(() => this.playAudio(true), 200);
                this.state.wasPlayingBeforeHidden = false;
            }
        }
    }

    toggleAudio() {
        const { audio } = this.elements;
        if (audio.paused) {
            this.setVolume(this.state.lastNonZeroVolume);
            this.playAudio();
        } else {
            audio.pause();
            this.state.isPlaying = false;
            this.updateIcon();
        }
    }

    setVolume(val) {
        const clamped = Math.max(0, Math.min(1, val));
        this.elements.audio.volume = clamped;
        if (clamped > 0) this.state.lastNonZeroVolume = clamped;
        this.updateVolumeUI(clamped);
        this.updateIcon();
    }

    updateIcon() {
        const { icon, audio } = this.elements;
        const { icons } = this.config;
        if (audio.paused || (audio.volume === 0 && !this.state.isMobile)) {
            icon.src = icons.off;
        } else {
            icon.src = icons.on;
        }
    }

    // --- CONTROL DE VOLUMEN ---

    createVolumeControl() {
        const initialVol = this.elements.audio.volume || 1;
        const wrapper = document.createElement('div');
        wrapper.className = 'volume-wrapper';
        wrapper.innerHTML = `
            <div class="volume-track">
                <div class="volume-level" style="height: ${initialVol * 100}%"></div>
                <div class="volume-handle" style="bottom: ${initialVol * 100}%"></div>
            </div>
        `;
        this.elements.container.appendChild(wrapper);
        this.elements.volumeWrapper = wrapper;
        this.elements.volumeTrack = wrapper.querySelector('.volume-track');
        this.elements.volumeLevel = wrapper.querySelector('.volume-level');
        this.elements.volumeHandle = wrapper.querySelector('.volume-handle');

        if (!this.state.isMobile) {
            const startSlide = (e) => this.startVolumeDrag(e);
            wrapper.addEventListener('mousedown', startSlide);
            ['mousedown', 'click'].forEach(evt =>
                wrapper.addEventListener(evt, e => e.stopPropagation(), { passive: false })
            );
        } else {
            wrapper.style.pointerEvents = 'none';
            wrapper.style.display = 'none'; 
        }

        requestAnimationFrame(() => {
            if(this.elements.volumeTrack) {
                this.state.slider.trackHeight = this.elements.volumeTrack.getBoundingClientRect().height || 52;
                this.updateVolumeWrapperPosition();
            }
        });
    }
    
    showVolumeIndicator() {
        if (!this.state.isMobile && this.elements.volumeWrapper) {
            this.elements.volumeWrapper.classList.add('is-visible');
        }
    }

    hideVolumeIndicator() {
        if (!this.state.isAdjustingVolume && this.elements.volumeWrapper) {
            this.elements.volumeWrapper.classList.remove('is-visible');
        }
    }

    updateVolumeUI(vol) {
        const pct = vol * 100;
        if (this.elements.volumeLevel) this.elements.volumeLevel.style.height = `${pct}%`;
        if (this.elements.volumeHandle) this.elements.volumeHandle.style.bottom = `${pct}%`;
    }

    updateVolumeWrapperPosition() {
        const { volumeWrapper, container } = this.elements;
        if (!volumeWrapper) return;
        const rectContainer = container.getBoundingClientRect();
        if (rectContainer.top - 68 < 0) {
            volumeWrapper.style.bottom = 'auto';
            volumeWrapper.style.top = '100%';
        } else {
            volumeWrapper.style.top = '';
            volumeWrapper.style.bottom = '100%';
        }
    }
    
    startVolumeDrag(e) {
        e.preventDefault(); e.stopPropagation();
        this.state.isAdjustingVolume = true;
        this.setWidgetActive(true);
        this.showVolumeIndicator(); 
        const isTouch = e.type === 'touchstart';
        const moveEvent = isTouch ? 'touchmove' : 'mousemove';
        const endEvent = isTouch ? 'touchend' : 'mouseup';
        this.state.slider.rect = this.elements.volumeTrack.getBoundingClientRect();
        this.state.slider.trackHeight = this.state.slider.rect.height || 1;
        this.handleVolumeMove(isTouch ? e.touches[0] : e); 
        this.rafVolume = null;
        
        const moveHandler = (ev) => {
            ev.preventDefault();
            this.lastVolumeEvent = isTouch ? ev.touches[0] : ev;
            if (!this.rafVolume) this.rafVolume = requestAnimationFrame(this.updateVolumeDrag);
        };
        const endHandler = () => {
            this.state.isAdjustingVolume = false;
            cancelAnimationFrame(this.rafVolume);
            this.rafVolume = null;
            document.removeEventListener(moveEvent, moveHandler);
            document.removeEventListener(endEvent, endHandler);
            this.scheduleInactive();
            this.hideVolumeIndicator();
        };
        document.addEventListener(moveEvent, moveHandler, { passive: false });
        document.addEventListener(endEvent, endHandler);
    }

    updateVolumeDrag() {
        if (!this.state.isAdjustingVolume || !this.lastVolumeEvent) return;
        this.handleVolumeMove(this.lastVolumeEvent);
        this.rafVolume = null;
    }
    
    handleVolumeMove(e) {
        const clientY = e.clientY;
        const { rect, trackHeight } = this.state.slider;
        const delta = rect.bottom - clientY; 
        const vol = delta / trackHeight;
        this.setVolume(vol);
    }

    // --- DRAG Y POSICIONAMIENTO ---

    setupUI() {
        const { container, icon, externalBtn } = this.elements;
        const startDrag = (e) => this.onDragStart(e);
        container.addEventListener('mousedown', startDrag);
        container.addEventListener('touchstart', startDrag, { passive: false }); 
        container.addEventListener('click', (e) => this.handleMainToggleClick(e));
        icon.addEventListener('click', (e) => this.handleMuteToggle(e));
        if (externalBtn) externalBtn.addEventListener('click', (e) => this.handleMainToggleClick(e));
        window.addEventListener('resize', this.handleResize);
        
        if (!this.state.isMobile) {
            container.addEventListener('mouseenter', () => { this.setWidgetActive(true); this.showVolumeIndicator(); });
            container.addEventListener('mouseleave', () => { this.scheduleInactive(200); this.hideVolumeIndicator(); });
        }
    }
    
    handleMainToggleClick(e) {
        if (e.type === 'click' && e.button !== 0) return; 
        e.stopPropagation();
        if (this.state.drag.dragJustEnded || this.state.isAdjustingVolume) return;
        this.setWidgetActive(true);
        this.toggleAudio();
        this.scheduleInactive();
    }
    
    handleMuteToggle(e) {
        e.stopPropagation();
        this.setWidgetActive(true);
        this.elements.tooltip.classList.remove('visible');
        const { audio } = this.elements;
        
        if (!this.state.isPlaying && audio.paused) {
            this.playAudio();
            return;
        }
        if (audio.volume > 0) {
            this.state.lastNonZeroVolume = audio.volume;
            this.setVolume(0);
        } else {
            this.setVolume(this.state.lastNonZeroVolume || 1);
        }
        this.scheduleInactive();
    }
    
    onDragStart(e) {
        if (this.state.isAdjustingVolume) return;
        const isTouch = e.type === 'touchstart';
        const evt = isTouch ? e.touches[0] : e;
        this.state.isDragging = true;
        this.state.drag.hasMoved = false;
        this.state.drag.dragJustEnded = false;
        this.elements.container.classList.add('is-dragging', 'is-active');
        const rect = this.elements.container.getBoundingClientRect();
        this.state.drag.startX = evt.clientX;
        this.state.drag.startY = evt.clientY;
        this.state.drag.initialLeft = rect.left;
        this.state.drag.initialTop = rect.top;
        const moveEvent = isTouch ? 'touchmove' : 'mousemove';
        const endEvent = isTouch ? 'touchend' : 'mouseup';
        this.rafDrag = null;

        const moveHandler = (ev) => {
            this.lastDragEvent = isTouch ? ev.touches[0] : ev;
            const deltaX = this.lastDragEvent.clientX - this.state.drag.startX;
            const deltaY = this.lastDragEvent.clientY - this.state.drag.startY;
            if (!this.state.drag.hasMoved && (Math.abs(deltaX) > this.config.touchDragThreshold || Math.abs(deltaY) > this.config.touchDragThreshold)) {
                this.state.drag.hasMoved = true;
            }
            if (this.state.drag.hasMoved) {
                if (ev.cancelable) ev.preventDefault(); 
                if (!this.rafDrag) this.rafDrag = requestAnimationFrame(this.updateDrag);
            }
        };

        const endHandler = () => {
            this.state.isDragging = false;
            cancelAnimationFrame(this.rafDrag);
            this.rafDrag = null;
            this.elements.container.classList.remove('is-dragging');
            document.removeEventListener(moveEvent, moveHandler);
            document.removeEventListener(endEvent, endHandler);
            if (this.state.drag.hasMoved) {
                this.snapToEdge();
                this.state.drag.dragJustEnded = true;
                setTimeout(() => { this.state.drag.dragJustEnded = false; }, 100);
            }
            this.scheduleInactive();
        };
        document.addEventListener(moveEvent, moveHandler, { passive: false });
        document.addEventListener(endEvent, endHandler);
    }

    updateDrag() {
        if (!this.state.isDragging || !this.lastDragEvent || !this.state.drag.hasMoved) return;
        const e = this.lastDragEvent;
        const deltaX = e.clientX - this.state.drag.startX;
        const deltaY = e.clientY - this.state.drag.startY;
        const newLeft = this.state.drag.initialLeft + deltaX;
        const newTop = this.state.drag.initialTop + deltaY;
        this.elements.container.style.left = `${newLeft}px`;
        this.elements.container.style.top = `${newTop}px`;
        this.elements.container.style.right = 'auto'; 
        this.elements.container.style.bottom = 'auto';
        this.updateVolumeWrapperPosition();
        this.rafDrag = null;
    }

    snapToEdge() {
        const rect = this.elements.container.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const margin = 20;
        let targetLeft;
        let targetTop = Math.max(margin, Math.min(vh - rect.height - margin, rect.top));
        if ((rect.left + rect.width / 2) < vw / 2) {
            targetLeft = margin;
        } else {
            targetLeft = vw - rect.width - margin;
        }
        this.elements.container.style.top = `${targetTop}px`;
        this.elements.container.style.left = `${targetLeft}px`;
        this.elements.container.style.right = 'auto'; 
        this.updateVolumeWrapperPosition();
    }
    
    // --- POPUP REFORZADO PARA IN-APP ---
    showPermissionPopup() {
        const overlay = document.createElement('div');
        // Estilos ultra-específicos para asegurar visibilidad en Instagram/TikTok
        Object.assign(overlay.style, { 
            position: 'fixed', 
            top: '0', left: '0', width: '100%', height: '100%', 
            zIndex: 2147483647, // Z-index máximo permitido en CSS
            background: 'rgba(0,0,0,0.55)', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            padding: '16px' 
        });
        
        overlay.innerHTML = `
            <div style="background:#fff; padding:20px; border-radius:12px; max-width:320px; width:90%; font-family:inherit; text-align:center; box-shadow: 0 4px 15px rgba(0,0,0,0.3);">
                <h3 style="margin:0 0 8px; font-size:18px;">¿Activar sonido de fondo?</h3>
                <div style="display:flex; justify-content:center; gap:8px; margin-top:8px;">
                    <button id="permisoNo" type="button" style="border:1px solid #d0d0d0; padding:8px 14px; border-radius:999px; background:#ffffff; cursor:pointer; font-size:13px;">No, gracias</button>
                    <button id="permisoSi" type="button" style="border:none; padding:8px 14px; border-radius:999px; background:#000000; color:#ffffff; cursor:pointer; font-size:13px;">Sí, activar sonido</button>
                </div>
            </div>
        `;
        document.body.appendChild(overlay);

        const close = (perm) => {
            this.savePermission(perm);
            if (perm === 'granted') {
                this.playAudio(false);
            }
            overlay.remove();
        };

        overlay.querySelector('#permisoSi').onclick = () => close('granted');
        overlay.querySelector('#permisoNo').onclick = () => close('denied');
        overlay.onclick = (e) => { if(e.target === overlay) close('denied'); };
    }

    // --- UTILIDADES ---

    checkIfMobile() {
        return /Mobi|Android|iPhone|iPod|Windows Phone|IEMobile|BlackBerry|Opera Mini/i.test(navigator.userAgent) || ('ontouchstart' in window && !/Macintosh/.test(navigator.userAgent));
    }
    
    debounce(func, wait) {
        let timeout;
        return function(...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    handleResize() {
        this.snapToEdge();
        this.updateVolumeWrapperPosition();
    }
}

// Inicialización
document.addEventListener('DOMContentLoaded', () => {
    if (typeof window.AudioWidgetInstance === 'undefined') {
        window.AudioWidgetInstance = new AudioWidget();
        window.AudioWidgetInstance.init();
    }
});

// Modal Golden Pass
document.addEventListener('DOMContentLoaded', () => {
  const boton = document.querySelector('.button.button--simple.focus-inset');
  if (!boton) return;

  // Habilitar botón
  boton.removeAttribute('aria-disabled');
  boton.style.cursor = 'pointer';

  // Crear elementos
  const overlay  = document.createElement('div');
  const modal    = document.createElement('div');
  const closeBtn = document.createElement('button');
  const img      = document.createElement('img');

  // Estilos del overlay
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0)',
    opacity: '0',
    visibility: 'hidden',
    transition: 'opacity 0.4s ease, background-color 0.4s ease',
    zIndex: '9999',
    backdropFilter: 'blur(3px)'
  });

  // Estilos del modal
  Object.assign(modal.style, {
    maxWidth: '90vw',
    borderRadius: '10px',
    transform: 'scale(0.8)',
    transition: 'transform 0.4s ease',
    boxShadow: '0 0 40px rgba(0, 0, 0, 0.5)',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  });

  // Estilos de la imagen
  Object.assign(img.style, {
    maxWidth: '90vw',
    maxHeight: '90vh',
    width: 'auto',
    height: 'auto',
    display: 'block',
    cursor: 'pointer'
  });

  // Estilos del botón de cierre (X)
  closeBtn.innerHTML = '&times;';
  closeBtn.setAttribute('aria-label', 'Cerrar');
  Object.assign(closeBtn.style, {
    position: 'absolute',
    top: '0',
    right: '0',
    width: '30px',
    height: '30px',
    borderRadius: '50%',          // círculo
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',   // fondo blanco
    color: '#000000',             // X negra
    fontSize: '28px',             // X más grande
    lineHeight: '1',
    cursor: 'pointer',
    zIndex: '10000',
    padding: '0',
    transform: 'translate(10%, -10%)' // centro del círculo en la esquina
  });

  modal.appendChild(closeBtn);
  modal.appendChild(img);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Media query y URLs
  const mediaQuery  = window.matchMedia('(max-width: 768px)');
  const URL_MOBILE  = 'https://cdn.shopify.com/s/files/1/0689/8135/8754/files/golden_pass_final.png?v=1760641771';
  const URL_DESKTOP = 'https://cdn.shopify.com/s/files/1/0689/8135/8754/files/golden_pass_web.png?v=1762822148';

  // Actualizar imagen según ancho
  function actualizarImagen() {
    const nuevaUrl = mediaQuery.matches ? URL_MOBILE : URL_DESKTOP;
    if (img.src !== nuevaUrl) {
      img.src = nuevaUrl;
    }
  }

  // Abrir modal
  function abrirModal() {
    actualizarImagen();
    overlay.style.visibility = 'visible';
    overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.85)';
    overlay.style.opacity = '1';

    requestAnimationFrame(() => {
      modal.style.transform = 'scale(1)';
    });
  }

  // Cerrar modal
  function cerrarModal() {
    modal.style.transform = 'scale(0.8)';
    overlay.style.opacity = '0';
    overlay.style.backgroundColor = 'rgba(0, 0, 0, 0)';

    setTimeout(() => {
      overlay.style.visibility = 'hidden';
    }, 400);
  }

  // Eventos
  boton.addEventListener('click', abrirModal);

  // Cerrar al hacer click fuera del modal
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      cerrarModal();
    }
  });

  // Cerrar al hacer click en la X
  closeBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    cerrarModal();
  });

  // Cerrar al hacer click/tap en la imagen
  img.addEventListener('click', (e) => {
    e.stopPropagation();
    cerrarModal();
  });

  // Cerrar con ESC
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      cerrarModal();
    }
  });

  // Cambios de tamaño dinámicos
  mediaQuery.addEventListener('change', actualizarImagen);

  // Inicializar imagen correcta al cargar
  actualizarImagen();
});

// WPP MOVIL
document.addEventListener('DOMContentLoaded', function() {
    
    // --- CONFIGURACIÓN ---
    const CONFIG = {
        phoneNumber: "573042721827",
        initialMessage: "ʙᴀᴅ ᴘᴇᴏᴘʟᴇ ᴄᴏᴍᴘᴀɴʏ 🏛️ \n\nQuiero hacer mi pedido 💳",
        sidePadding: 20,
        buttonSize: 45 // Debe coincidir con el CSS
    };

    const widget = document.getElementById('custom-contact-widget');
    if (!widget) return; // Si no existe el HTML, salir

    // --- POSICIONAMIENTO INICIAL (Mobile Check) ---
    function setInitialPosition() {
        if (window.innerWidth <= 768) {
            const header = document.querySelector('header') || 
                           document.querySelector('.site-header') || 
                           document.querySelector('#shopify-section-header');
            
            let headerHeight = 0;
            if (header) {
                headerHeight = header.getBoundingClientRect().height;
            }

            // Arriba a la derecha
            const initialTop = headerHeight + CONFIG.sidePadding;
            const initialLeft = window.innerWidth - CONFIG.buttonSize - CONFIG.sidePadding;

            widget.style.top = initialTop + 'px';
            widget.style.left = initialLeft + 'px';
            widget.classList.add('mobile-positioned');
        }
    }
    
    setInitialPosition();

    // --- LÓGICA DRAG & DROP ---
    let isDragging = false;
    let startX, startY, initialLeft, initialTop;
    let hasMoved = false;

    widget.addEventListener('mousedown', startDrag);
    window.addEventListener('mousemove', drag);
    window.addEventListener('mouseup', endDrag);

    widget.addEventListener('touchstart', startDrag, {passive: false});
    window.addEventListener('touchmove', drag, {passive: false});
    window.addEventListener('touchend', endDrag);

    function startDrag(e) {
        if(e.type === 'touchstart') e.preventDefault(); 

        isDragging = true;
        hasMoved = false;
        
        const clientX = e.clientX || e.touches[0].clientX;
        const clientY = e.clientY || e.touches[0].clientY;

        startX = clientX;
        startY = clientY;

        const rect = widget.getBoundingClientRect();
        initialLeft = rect.left;
        initialTop = rect.top;

        widget.classList.remove('widget-snap-animate');
        widget.style.transition = 'none'; 
        widget.style.cursor = 'grabbing';
        widget.style.transform = 'none'; 
    }

    function drag(e) {
        if (!isDragging) return;

        const clientX = e.clientX || (e.touches ? e.touches[0].clientX : 0);
        const clientY = e.clientY || (e.touches ? e.touches[0].clientY : 0);

        const deltaX = clientX - startX;
        const deltaY = clientY - startY;

        if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
            hasMoved = true;
        }

        let newLeft = initialLeft + deltaX;
        let newTop = initialTop + deltaY;

        widget.style.left = `${newLeft}px`;
        widget.style.top = `${newTop}px`;
    }

    function endDrag(e) {
        if (!isDragging) return;
        isDragging = false;
        
        widget.style.cursor = 'grab';
        widget.style.transition = ''; 

        // CLICK: Si no se movió, abre WhatsApp
        if (!hasMoved) {
            if (window.innerWidth > 768) {
                widget.style.transform = 'translateY(-50%)'; 
            } else {
                widget.style.transform = 'none';
            }
            
            const url = `https://wa.me/${CONFIG.phoneNumber}?text=${encodeURIComponent(CONFIG.initialMessage)}`;
            window.open(url, '_blank');
            return;
        }

        // SNAP: Si se soltó, ajustar a los bordes
        const rect = widget.getBoundingClientRect();
        const centerX = rect.left + (rect.width / 2);
        const screenCenter = window.innerWidth / 2;

        let finalTop = rect.top;
        if (finalTop < 0) finalTop = CONFIG.sidePadding;
        if (finalTop > window.innerHeight - CONFIG.buttonSize) finalTop = window.innerHeight - CONFIG.buttonSize - CONFIG.sidePadding;

        let finalLeft;
        if (centerX < screenCenter) {
            finalLeft = CONFIG.sidePadding; 
        } else {
            finalLeft = window.innerWidth - CONFIG.buttonSize - CONFIG.sidePadding; 
        }

        widget.classList.add('widget-snap-animate');
        widget.style.left = `${finalLeft}px`;
        widget.style.top = `${finalTop}px`;
        widget.style.transform = 'none';
    }

    // Resize
    window.addEventListener('resize', () => {
        const rect = widget.getBoundingClientRect();
        if (rect.left > window.innerWidth / 2) {
            widget.style.left = `${window.innerWidth - CONFIG.buttonSize - CONFIG.sidePadding}px`;
        }
    });
});